import { computed, ref, shallowRef, triggerRef } from 'vue'
import { defineStore } from 'pinia'
import {
  fetchBreadth,
  fetchDashboard,
  fetchInstrumentProfile,
  fetchMemberPositions,
  fetchOptionChain,
  fetchOptionSummary,
  fetchTermStructure,
  openMarketStream,
  updateInstrumentProfile
} from '../api/client'
import type {
  BreadthData,
  DashboardData,
  InstrumentProfile,
  MemberPositionsData,
  OptionChainData,
  OptionSummaryData,
  StreamQuotePayload,
  TermStructureData
} from '../types'

const PERIOD_KEY = 'imdesk.period'

export const useMarketStore = defineStore('market', () => {
  const dashboard = shallowRef<DashboardData | null>(null)
  const profile = shallowRef<InstrumentProfile | null>(null)
  const termStructure = shallowRef<TermStructureData | null>(null)
  const memberPositions = shallowRef<MemberPositionsData | null>(null)
  const optionSummary = shallowRef<OptionSummaryData | null>(null)
  const optionChain = shallowRef<OptionChainData | null>(null)
  const breadth = shallowRef<BreadthData | null>(null)
  const evidenceErrors = ref<Record<string, string>>({})
  const loading = ref(false)
  const refreshing = ref(false)
  const error = ref<string | null>(null)
  const connection = ref<'connecting' | 'live' | 'reconnecting' | 'offline'>('connecting')
  const sourceMode = ref<'pending' | 'sample' | 'live'>('pending')
  const switchingProduct = ref<string | null>(null)
  const contract = ref('')
  const period = ref(localStorage.getItem(PERIOD_KEY) || '15m')

  let controller: AbortController | null = null
  let reconnectTimer: number | null = null
  let initialized = false
  let refreshingTask: Promise<void> | null = null

  const dataMode = computed(() => sourceMode.value)
  const isSample = computed(() => sourceMode.value === 'sample')
  const currentQuote = computed(() => dashboard.value?.futures_quote ?? null)
  const currentSpot = computed(() => dashboard.value?.spot_quote ?? null)
  const currentBasis = computed(() => dashboard.value?.basis ?? null)
  const availableProducts = computed(() => profile.value?.available_products ?? [])
  const optionProduct = computed(() => profile.value?.option_product ?? null)

  async function loadDashboard(showLoading = false): Promise<void> {
    if (!contract.value) {
      throw new Error('尚未确定当前期货合约')
    }
    if (refreshingTask) return refreshingTask
    if (showLoading) loading.value = true
    else refreshing.value = true
    error.value = null
    refreshingTask = (async () => {
      try {
        const result = await fetchDashboard(contract.value, period.value)
        dashboard.value = result.data
        sourceMode.value = result.meta.data_mode
        connection.value = 'live'
      } catch (err) {
        error.value = err instanceof Error ? err.message : '无法加载看板数据'
        connection.value = 'offline'
        throw err
      } finally {
        loading.value = false
        refreshing.value = false
        refreshingTask = null
      }
    })()
    return refreshingTask
  }

  function applyQuoteUpdate(payload: StreamQuotePayload): void {
    if (!dashboard.value) return
    const quote = payload.futures_quotes.find(item => item.contract_id === contract.value)
    if (quote) dashboard.value.futures_quote = quote
    dashboard.value.spot_quote = payload.spot_quote
    triggerRef(dashboard)
  }

  function scheduleReconnect(): void {
    if (reconnectTimer) window.clearTimeout(reconnectTimer)
    connection.value = 'reconnecting'
    reconnectTimer = window.setTimeout(() => {
      void loadDashboard().catch(() => undefined).finally(startStream)
    }, 3500)
  }

  function startStream(): void {
    controller?.abort()
    controller = new AbortController()
    connection.value = dashboard.value ? 'reconnecting' : 'connecting'
    const activeController = controller
    void openMarketStream(
      contract.value,
      profile.value?.spot_index_code ?? '',
      {
        ready: () => {
          connection.value = 'live'
        },
        quotes: applyQuoteUpdate,
        heartbeat: () => {
          if (connection.value !== 'offline') connection.value = 'live'
        }
      },
      activeController.signal
    ).catch(err => {
      if (activeController.signal.aborted) return
      error.value = err instanceof Error ? err.message : '实时通道已断开'
      scheduleReconnect()
    })
  }

  function resetProductData(): void {
    dashboard.value = null
    termStructure.value = null
    memberPositions.value = null
    optionSummary.value = null
    optionChain.value = null
    breadth.value = null
    evidenceErrors.value = {}
    error.value = null
  }

  async function initialize(): Promise<void> {
    if (initialized) return
    initialized = true
    loading.value = true
    try {
      profile.value = (await fetchInstrumentProfile()).data
      contract.value = profile.value.active_contract
      await loadDashboard(true)
      connection.value = 'live'
      startStream()
    } catch {
      error.value = error.value || '无法读取当前期货品种配置'
      connection.value = 'offline'
    } finally {
      loading.value = false
    }
  }

  async function retry(): Promise<void> {
    if (!profile.value) {
      initialized = false
      await initialize()
      return
    }
    try {
      await loadDashboard(true)
      startStream()
    } catch {
      // Keep the actionable error screen visible.
    }
  }

  async function setContract(value: string): Promise<void> {
    if (value === contract.value) return
    contract.value = value
    await loadDashboard()
    startStream()
  }

  async function setProduct(product: string): Promise<void> {
    if (switchingProduct.value || product === profile.value?.product) return
    switchingProduct.value = product
    try {
      const result = await updateInstrumentProfile(product)
      profile.value = result.data
      contract.value = result.data.active_contract
      resetProductData()
      await loadDashboard(true)
      startStream()
    } finally {
      switchingProduct.value = null
    }
  }

  async function setPeriod(value: string): Promise<void> {
    if (value === period.value) return
    period.value = value
    localStorage.setItem(PERIOD_KEY, value)
    await loadDashboard()
  }

  async function loadTermStructure(): Promise<TermStructureData | null> {
    if (termStructure.value) return termStructure.value
    if (!profile.value) return null
    const response = await fetchTermStructure(profile.value.product)
    termStructure.value = response.data
    return termStructure.value
  }

  function setEvidenceError(key: string, error: unknown, fallback: string): void {
    evidenceErrors.value = {
      ...evidenceErrors.value,
      [key]: error instanceof Error ? error.message : fallback
    }
  }

  function clearEvidenceError(key: string): void {
    if (!(key in evidenceErrors.value)) return
    const next = { ...evidenceErrors.value }
    delete next[key]
    evidenceErrors.value = next
  }

  async function loadBasisDetails(): Promise<void> {
    if (!profile.value) return
    const activeProfile = profile.value
    const tasks: Promise<void>[] = []
    if (termStructure.value) {
      clearEvidenceError('basis')
    } else {
      tasks.push((async () => {
        try {
          termStructure.value = (await fetchTermStructure(activeProfile.product)).data
          clearEvidenceError('basis')
        } catch (error) {
          setEvidenceError('basis', error, '期限结构不可用')
        }
      })())
    }
    if (breadth.value) {
      clearEvidenceError('breadth')
    } else {
      tasks.push((async () => {
        try {
          breadth.value = (await fetchBreadth(activeProfile.breadth_universe)).data
          clearEvidenceError('breadth')
        } catch (error) {
          setEvidenceError('breadth', error, '市场宽度不可用')
        }
      })())
    }
    await Promise.all(tasks)
  }

  async function loadPositionDetails(): Promise<void> {
    if (memberPositions.value) {
      clearEvidenceError('positions')
      return
    }
    try {
      memberPositions.value = (await fetchMemberPositions(contract.value)).data
      clearEvidenceError('positions')
    } catch (error) {
      setEvidenceError('positions', error, '席位数据不可用')
    }
  }

  async function loadOptionDetails(): Promise<void> {
    const activeProfile = profile.value
    if (!activeProfile?.option_product || !activeProfile.option_expiry) {
      optionSummary.value = null
      optionChain.value = null
      setEvidenceError(
        'options',
        null,
        `${activeProfile?.display_name ?? '当前品种'}暂无对应场内期权`
      )
      return
    }
    const tasks: Promise<void>[] = []
    if (optionSummary.value) {
      clearEvidenceError('optionsSummary')
    } else {
      tasks.push((async () => {
        try {
          optionSummary.value = (await fetchOptionSummary(
            activeProfile.option_product!,
            activeProfile.option_expiry!
          )).data
          clearEvidenceError('optionsSummary')
        } catch (error) {
          setEvidenceError('optionsSummary', error, '期权摘要不可用')
        }
      })())
    }
    if (optionChain.value) {
      clearEvidenceError('optionsChain')
    } else {
      tasks.push((async () => {
        try {
          optionChain.value = (await fetchOptionChain(
            activeProfile.option_product!,
            activeProfile.option_expiry!
          )).data
          clearEvidenceError('optionsChain')
        } catch (error) {
          setEvidenceError('optionsChain', error, '期权链不可用')
        }
      })())
    }
    await Promise.all(tasks)
  }

  async function loadOptionSummaryDetails(): Promise<void> {
    const activeProfile = profile.value
    if (!activeProfile?.option_product || !activeProfile.option_expiry) {
      optionSummary.value = null
      setEvidenceError(
        'optionsSummary',
        null,
        `${activeProfile?.display_name ?? '当前品种'}暂无对应场内期权`
      )
      return
    }
    if (optionSummary.value) {
      clearEvidenceError('optionsSummary')
      return
    }
    try {
      optionSummary.value = (await fetchOptionSummary(
        activeProfile.option_product,
        activeProfile.option_expiry
      )).data
      clearEvidenceError('optionsSummary')
    } catch (error) {
      setEvidenceError('optionsSummary', error, '期权摘要不可用')
    }
  }

  function dispose(): void {
    controller?.abort()
    if (reconnectTimer) window.clearTimeout(reconnectTimer)
  }

  return {
    dashboard,
    profile,
    termStructure,
    memberPositions,
    optionSummary,
    optionChain,
    breadth,
    evidenceErrors,
    loading,
    refreshing,
    error,
    connection,
    contract,
    period,
    dataMode,
    isSample,
    currentQuote,
    currentSpot,
    currentBasis,
    availableProducts,
    optionProduct,
    switchingProduct,
    initialize,
    retry,
    loadDashboard,
    setContract,
    setProduct,
    setPeriod,
    loadTermStructure,
    loadBasisDetails,
    loadPositionDetails,
    loadOptionDetails,
    loadOptionSummaryDetails,
    dispose
  }
})
